import {
  requestClubs as fixtureClubs,
  seedRequests,
  seedMessages,
  seedMemberInvoices,
} from './virtusDemo/fixtures';
import { RequestStatus, isTerminal } from './virtusDemo/requestStatus';

const STORAGE_KEY = 'virtusDemo.v3';

const loadFromStorage = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    return null;
  }
};

const saveToStorage = (state) => {
  try {
    const snapshot = {
      bookingRequests: state.bookingRequests,
      conciergeMessages: state.conciergeMessages,
      memberInvoices: state.memberInvoices,
      seeded: state.seeded,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch (e) {
    // localStorage unavailable or full — demo will work in-memory only
  }
};

const nowIso = () => new Date().toISOString();

const generateRequestId = () => {
  const n = 1043 + Math.floor(Math.random() * 900);
  return `REQ-${n}`;
};

const generateMessageId = () => `MSG-${Date.now()}`;

// Canned concierge replies for the demo — rotated so a sent text always gets answered.
const CONCIERGE_REPLIES = [
  "Got it — leave it with us. We'll text you back shortly with a few ranked options.",
  'Thanks! The concierge is on it — expect a reply with available tee times very soon.',
  "On the case. We'll reach out to the club and come straight back to you here.",
  "Received — we'll line up some options and confirm with you in a moment.",
];

const virtusDemoStore = {
  namespaced: true,

  state: () => ({
    requestClubs: fixtureClubs,
    bookingRequests: [],
    conciergeMessages: [],
    memberInvoices: [],
    draftRequest: null,
    seeded: false,
    resetCounter: 0,
  }),

  getters: {
    messageHistory: state => [...state.conciergeMessages]
      .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt)),
    unreadConciergeReplies: state => state.conciergeMessages
      .filter(m => m.reply && !m.reply.read).length,
    memberInvoiceHistory: state => [...state.memberInvoices]
      .sort((a, b) => new Date(b.issuedAt) - new Date(a.issuedAt)),
    outstandingInvoices: state => state.memberInvoices.filter(i => i.status === 'Outstanding'),
    settledInvoices: state => state.memberInvoices.filter(i => i.status === 'Paid'),
    pendingRequests: state => state.bookingRequests.filter(r => !isTerminal(r.status)),
    terminalRequests: state => state.bookingRequests.filter(r => isTerminal(r.status)),
    requestById: state => id => state.bookingRequests.find(r => r.id === id),
    clubById: state => id => state.requestClubs.find(c => c.id === id),
    countsByStatus: (state) => {
      const counts = {
        [RequestStatus.Requested]: 0,
        [RequestStatus.PendingClub]: 0,
        [RequestStatus.Accepted]: 0,
        [RequestStatus.Declined]: 0,
        [RequestStatus.Expired]: 0,
      };
      state.bookingRequests.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
      return counts;
    },
  },

  mutations: {
    hydrate(state, snapshot) {
      if (!snapshot) return;
      if (snapshot.bookingRequests) state.bookingRequests = snapshot.bookingRequests;
      if (snapshot.conciergeMessages) state.conciergeMessages = snapshot.conciergeMessages;
      if (snapshot.memberInvoices) state.memberInvoices = snapshot.memberInvoices;
      state.seeded = !!snapshot.seeded;
    },

    setSeeded(state, value) {
      state.seeded = value;
    },

    setMessages(state, list) {
      state.conciergeMessages = list;
    },

    addMessage(state, message) {
      state.conciergeMessages = [message, ...state.conciergeMessages];
    },

    setReply(state, { id, reply }) {
      state.conciergeMessages = state.conciergeMessages
        .map(m => (m.id === id ? { ...m, reply } : m));
    },

    markRepliesRead(state) {
      state.conciergeMessages = state.conciergeMessages
        .map(m => (m.reply ? { ...m, reply: { ...m.reply, read: true } } : m));
    },

    setMemberInvoices(state, list) {
      state.memberInvoices = list;
    },

    settleInvoice(state, { id, at }) {
      state.memberInvoices = state.memberInvoices
        .map(i => (i.id === id ? { ...i, status: 'Paid', paidAt: at } : i));
    },

    setBookingRequests(state, list) {
      state.bookingRequests = list;
    },

    addBookingRequest(state, request) {
      state.bookingRequests = [request, ...state.bookingRequests];
    },

    updateBookingRequest(state, patch) {
      state.bookingRequests = state.bookingRequests.map(r => (r.id === patch.id ? { ...r, ...patch } : r));
    },

    appendTimelineEntry(state, { id, entry }) {
      state.bookingRequests = state.bookingRequests.map((r) => {
        if (r.id !== id) return r;
        return { ...r, timeline: [...(r.timeline || []), entry] };
      });
    },

    appendCommunication(state, { id, entry }) {
      state.bookingRequests = state.bookingRequests.map((r) => {
        if (r.id !== id) return r;
        return { ...r, communications: [...(r.communications || []), entry] };
      });
    },

    resetAll(state) {
      state.bookingRequests = [];
      state.conciergeMessages = [];
      state.memberInvoices = [];
      state.draftRequest = null;
      state.seeded = false;
      state.resetCounter += 1;
    },

    setDraft(state, draft) {
      state.draftRequest = draft;
    },

    clearDraft(state) {
      state.draftRequest = null;
    },
  },

  actions: {
    init({ state, commit, dispatch }) {
      const snapshot = loadFromStorage();
      if (snapshot) commit('hydrate', snapshot);
      if (!state.seeded) {
        dispatch('seed');
      } else {
        // Back-fill features added after a demo was first seeded.
        if (!state.conciergeMessages || state.conciergeMessages.length === 0) {
          commit('setMessages', seedMessages());
        }
        if (!state.memberInvoices || state.memberInvoices.length === 0) {
          commit('setMemberInvoices', seedMemberInvoices());
        }
        saveToStorage(state);
      }
    },

    seed({ commit, state }) {
      const requests = seedRequests();
      commit('setBookingRequests', requests);
      commit('setMessages', seedMessages());
      commit('setMemberInvoices', seedMemberInvoices());
      commit('setSeeded', true);
      saveToStorage(state);
    },

    settleInvoice({ commit, state }, id) {
      commit('settleInvoice', { id, at: nowIso() });
      saveToStorage(state);
      return Promise.resolve({ id });
    },

    markRepliesRead({ commit, state }) {
      commit('markRepliesRead');
      saveToStorage(state);
    },

    // Member sends a text to the concierge desk. Adds the message immediately, then
    // simulates a single reply a couple of seconds later.
    sendMessage({ commit, state }, body) {
      const text = (body || '').trim();
      if (!text) return Promise.resolve(null);

      const id = generateMessageId();
      commit('addMessage', {
        id,
        body: text,
        sentAt: nowIso(),
        reply: null,
      });
      saveToStorage(state);

      setTimeout(() => {
        const reply = CONCIERGE_REPLIES[state.conciergeMessages.length % CONCIERGE_REPLIES.length];
        commit('setReply', { id, reply: { body: reply, at: nowIso(), read: false } });
        saveToStorage(state);
      }, 2200);

      return Promise.resolve({ id });
    },

    reset({ commit, dispatch, state }) {
      commit('resetAll');
      dispatch('seed');
      saveToStorage(state);
    },

    createRequest({ commit, state }, payload) {
      return new Promise((resolve) => {
        const id = generateRequestId();
        const createdAt = nowIso();
        const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
        const request = {
          id,
          memberName: payload.memberName || 'Virtus Member',
          memberDisplayFirst: payload.memberDisplayFirst || '',
          clubId: payload.clubId,
          clubName: payload.clubName,
          preferredDate: payload.preferredDate,
          window: payload.window,
          choices: payload.choices,
          flex: payload.flex || { anyTimeInWindow: false, anyDayThisWeek: false },
          partySize: payload.partySize || 2,
          notes: payload.notes || '',
          status: RequestStatus.Requested,
          createdAt,
          expiresAt,
          acceptedOption: null,
          timeline: [{ status: RequestStatus.Requested, at: createdAt, by: 'member' }],
          communications: [],
        };
        commit('addBookingRequest', request);
        saveToStorage(state);

        setTimeout(() => {
          const transitionAt = nowIso();
          commit('updateBookingRequest', { id, status: RequestStatus.PendingClub });
          commit('appendTimelineEntry', {
            id,
            entry: {
              status: RequestStatus.PendingClub,
              at: transitionAt,
              by: 'system',
              note: 'Concierge email dispatched',
            },
          });
          commit('appendCommunication', {
            id,
            entry: {
              at: transitionAt,
              channel: 'email',
              direction: 'out',
              subject: `Virtus Member tee time request — ${request.clubName}`,
              target: 'concierge mailbox',
            },
          });
          saveToStorage(state);
          resolve({ request });
        }, 2000);
      });
    },

    expireRequest({ commit, state, getters }, requestId) {
      const req = getters.requestById(requestId);
      if (!req || isTerminal(req.status)) return;
      const at = nowIso();
      commit('updateBookingRequest', { id: requestId, status: RequestStatus.Expired });
      commit('appendTimelineEntry', {
        id: requestId,
        entry: {
          status: RequestStatus.Expired, at, by: 'system', note: 'Acceptance window elapsed',
        },
      });
      saveToStorage(state);
    },

    // Demo-only: simulates what the club/admin surface (in admintoolsnuxt) would push back
    // to the member app. Used by the walkthrough narrator to animate status change after
    // the club accepts via tokenised link in the other app.
    simulateClubResponse({ commit, state, getters }, { requestId, decision, optionIdx }) {
      const req = getters.requestById(requestId);
      if (!req || isTerminal(req.status)) return;
      const at = nowIso();
      if (decision === 'accept') {
        commit('updateBookingRequest', {
          id: requestId,
          status: RequestStatus.Accepted,
          acceptedOption: optionIdx,
        });
        commit('appendTimelineEntry', {
          id: requestId,
          entry: {
            status: RequestStatus.Accepted,
            at,
            by: 'club',
            note: `Club selected Option ${optionIdx}`,
          },
        });
        commit('appendCommunication', {
          id: requestId,
          entry: {
            at,
            channel: 'web',
            direction: 'in',
            subject: `Club selected Option ${optionIdx}`,
            target: 'acceptance link',
          },
        });
      } else if (decision === 'decline') {
        commit('updateBookingRequest', { id: requestId, status: RequestStatus.Declined });
        commit('appendTimelineEntry', {
          id: requestId,
          entry: {
            status: RequestStatus.Declined, at, by: 'club', note: 'Club declined request',
          },
        });
      }
      saveToStorage(state);
    },
  },
};

export { virtusDemoStore };
